import type { SignalTone } from '../../theme/tokens';

/** One row of `GET /admin/organizations/store-accounts` (src/services/adminStoreAccountsService.ts). */
export type SubscriptionState = 'active' | 'trialing' | 'past_due' | 'canceled' | 'complimentary' | 'none';

export interface StoreAccountRow {
  id: string;
  name: string;
  website: string | null;
  city: string | null;
  state: string | null;
  status: string;
  createdAt: string | null;
  planTier: string | null;
  planName: string | null;
  grantedFeatureCount: number;
  hasGrant: boolean;
  accessGrant: { kind: string; tier: string | null; expiresAt: string | null; fullAccess: boolean } | null;
  subscription: {
    state: SubscriptionState;
    status: string | null;
    amount: number | null;
    interval: string | null;
    currentPeriodEnd: string | null;
    trialEnd: string | null;
    stripeSubscriptionId: string | null;
  };
  seats?: { used: number; limit: number; pending: number; tier: string | null };
  lastActive?: { at: string | null; userName: string | null };
}

export interface StoreAccountPage {
  rows: StoreAccountRow[];
  total: number;
  statusCounts: Record<string, number>;
  pendingReview: Array<{ id: string; name: string; status: string; createdAt: string | null }>;
  page: number;
  limit: number;
}

export const STORE_TYPES = ['camera_store', 'store', 'retail'];
export const ALL_STATUSES = ['active', 'pending_approval', 'pending_verification', 'inactive', 'suspended', 'rejected'] as const;
export const DEFAULT_STATUSES: string[] = ['active', 'pending_approval', 'pending_verification'];

export const PLAN_FILTERS = [
  { value: 'any', label: 'Any plan' },
  { value: 'free', label: 'Free Preview' },
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'intelligence', label: 'Intelligence' },
  { value: 'complete', label: 'Complete' },
  { value: 'grant', label: 'Has access grant' },
] as const;

export const SUBSCRIPTION_FILTERS = [
  { value: 'any', label: 'Any subscription' },
  { value: 'active', label: 'Active' },
  { value: 'trialing', label: 'Trialing' },
  { value: 'past_due', label: 'Past due' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'complimentary', label: 'Complimentary' },
  { value: 'none', label: 'No subscription' },
] as const;

export const SORTS = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'name', label: 'Name A–Z' },
  { value: 'lastActive', label: 'Last active' },
  { value: 'newest', label: 'Newest' },
  { value: 'seats', label: 'Seats used (%)' },
] as const;

const PLAN_LABELS: Record<string, string> = {
  free: 'Free Preview',
  starter: 'Starter',
  pro: 'Pro',
  intelligence: 'Intelligence',
  complete: 'Complete',
  full: 'Full access',
};

export const planLabel = (tier: string | null | undefined): string =>
  tier ? PLAN_LABELS[tier] || tier.charAt(0).toUpperCase() + tier.slice(1) : 'No plan';

export const planTone = (tier: string | null | undefined): SignalTone =>
  !tier || tier === 'free' ? 'neutral' : 'info';

export const SUBSCRIPTION_LABELS: Record<SubscriptionState, string> = {
  active: 'Active',
  trialing: 'Trialing',
  past_due: 'Past due',
  canceled: 'Canceled',
  complimentary: 'Complimentary',
  none: 'No subscription',
};

export const subscriptionTone = (state: SubscriptionState): SignalTone => {
  switch (state) {
    case 'active':
      return 'positive';
    case 'trialing':
      return 'info';
    case 'past_due':
      return 'problem';
    default:
      return 'neutral';
  }
};

export const grantKindLabel = (kind: string): string =>
  kind === 'trial' ? 'Trial' : kind === 'beta' ? 'Beta' : 'Comp';

export const daysLeft = (iso: string | null | undefined, now: number = Date.now()): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - now;
  return Number.isNaN(ms) ? null : Math.max(0, Math.ceil(ms / 86_400_000));
};

export const shortDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';

export const longDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

export const relativeTime = (iso: string | null | undefined, now: number = Date.now()): string => {
  if (!iso) return 'Never';
  const ms = now - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'Never';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 60) return `${days} day${days === 1 ? '' : 's'} ago`;
  return `${Math.floor(days / 30)} months ago`;
};

export const isStaleActivity = (iso: string | null | undefined, now: number = Date.now()): boolean =>
  !!iso && now - new Date(iso).getTime() > 14 * 86_400_000;

/** "$199/mo" or "$1,990/yr": the plan's list price in its own interval; empty when unpriced. */
export const formatPlanPrice = (sub: Pick<StoreAccountRow['subscription'], 'amount' | 'interval'>): string => {
  if (!sub.amount) return '';
  const amount = Number.isInteger(sub.amount) ? String(sub.amount) : sub.amount.toFixed(2);
  return `$${amount}/${sub.interval === 'yearly' ? 'yr' : 'mo'}`;
};

/** "$199/mo · Renews Nov 17", "Trial ends Oct 22", "Complimentary grant". */
export const subscriptionNote = (sub: StoreAccountRow['subscription']): string => {
  const price = formatPlanPrice(sub);
  let event = '';
  if (sub.state === 'trialing' && sub.trialEnd) event = `Trial ends ${shortDate(sub.trialEnd)}`;
  else if (sub.state === 'past_due') event = 'Payment failed';
  else if (sub.state === 'active' && sub.currentPeriodEnd) event = `Renews ${shortDate(sub.currentPeriodEnd)}`;
  else if (sub.state === 'canceled') event = 'No live subscription';
  return [price, event].filter(Boolean).join(' · ');
};

export type SeatsTone = 'normal' | 'caution' | 'problem';

/** Bar tone for seats used; a limit of -1 is unlimited and never warns. */
export const seatsTone = (used: number, limit: number): SeatsTone => {
  if (limit <= 0) return 'normal';
  const ratio = used / limit;
  if (ratio >= 1) return 'problem';
  if (ratio >= 0.8) return 'caution';
  return 'normal';
};

export const statusFilterLabel = (statuses: string[]): string => {
  const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((s) => b.includes(s));
  if (sameSet(statuses, DEFAULT_STATUSES)) return 'Active + Pending';
  if (statuses.length === 0 || sameSet(statuses, [...ALL_STATUSES])) return 'All';
  return statuses.map((s) => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())).join(', ');
};

export const isDefaultStatuses = (statuses: string[]): boolean =>
  statuses.length === DEFAULT_STATUSES.length && DEFAULT_STATUSES.every((s) => statuses.includes(s));

export const storeStatusTone = (status?: string): SignalTone => {
  switch (status) {
    case 'active':
      return 'positive';
    case 'pending_approval':
    case 'pending_verification':
      return 'caution';
    case 'suspended':
    case 'rejected':
      return 'problem';
    default:
      return 'neutral';
  }
};
